import { useRef, useEffect, useState } from "react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  availableProgressStatusOfBilling,
  availableSuccessStatusOfBilling,
  BillingStatusEnum, PROCESS_INSTANCE_QUERY
} from "@/src/shared/const";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { API_BASE_URL, axiosInstance, BASE_URLS } from "@/src/shared/api";
import { closeLoadingModal, openLoadingModal, openErrorModal, closeErrorModal } from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import {
  loadingModalDefaultProps
} from "@/src/components/pages/StepPage/components/apiCalls/Modals/defaultModalsProps";
import { createStore } from "zustand";
import { useTextUsModalStore } from "@/src/stores/useTextUsModalStore";
import {useSearchParams} from "next/navigation";
import { logError } from "@/src/api/server-actions/analytics";

interface UseBillingPollingSSEProps {
  pollingInterval?: number;
  maxRequests?: number;
  handleNavigateAfterBilling?: () => Promise<void>;
}

export enum TransactionStatusEnum {
  BILLING_FAILED = 'FAILED',
  BILLING_IN_PROGRESS = 'IN_PROGRESS',
  SIGNING_IN_PROGRESS = 'SIGNING_IN_PROGRESS',
  FAIL_SINGING = 'FAIL_SINGING',
  CONTRACT_SIGNED = 'CONTRACT_SIGNED',
}

interface BillingStatusResponse {
  billIdentifier: string;
  billStatusCode: BillingStatusEnum;
  transactionStatus: TransactionStatusEnum;
  isMainTransaction: boolean;
  createdDate?: string;
  isExpiredBilling?: boolean
}

export const fetchBillingStatus = async (billId: string | undefined): Promise<BillingStatusResponse | null> => {
  if (!billId) return null;
  const response = await axiosInstance.get(`${BASE_URLS.BILLING}/transaction-status/${billId}`);
  return response.data;
};

export const transactionStatusStore = createStore<{transactionStatus: TransactionStatusEnum|null }>(() => ({
  transactionStatus: null
}))

/**
 * Hook for polling and SSE about billing status.
 * @param pollingInterval - Interval for polling in milliseconds
 * @param maxRequests - Maximum number of requests for polling
 * @param handleNavigateAfterBilling - Call once when status changes from Progress to Success
 */
export function useBillingPollingSSE({
   pollingInterval = 5000, // 5 секунд
   maxRequests = 240, // 20 хвилин
   handleNavigateAfterBilling,
}: UseBillingPollingSSEProps) {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);
  const processInstance = useProcessInstanceStore(
    (state) => state.processInstance
  );
  const openTextUsModal = useTextUsModalStore((state) => state.open);

  const [response, setResponse] = useState<BillingStatusResponse | null>(null);

  // const policyPrintouts = processInstance?.contextInstance.variables.find((variable) => variable.key === "policyPrintouts")?.value;
  // const policy = Boolean(typeof policyPrintouts === "string" ? policyPrintouts?.includes("uuid") : null);
  const policy = processInstance?.activeStep.code.includes("thank")
  const billIdVariable = processInstance?.contextInstance.variables.find((variable) => variable.key === "billId")?.value;
  const billId = !policy ? processInstance?.order?.transaction?.billId || billIdVariable : null;
  const billStatus = response?.billStatusCode || processInstance?.order?.transaction?.status as BillingStatusEnum;
  const orderStatus = processInstance?.order?.status

  const isErrorModalClose = useRef<boolean>(false);

  // Counter for polling requests
  const pollingCountRef = useRef<number>(0);

  // Flag for navigation
  const hasNavigatedRef = useRef<boolean>(!!policy);

  const isTransactionProgressStatus = response ? [TransactionStatusEnum.BILLING_IN_PROGRESS, TransactionStatusEnum.SIGNING_IN_PROGRESS]?.includes(response?.transactionStatus) : true;
  const isProgressStatus = billStatus
      ? (availableProgressStatusOfBilling.includes(billStatus) || isTransactionProgressStatus)
      : false;

  const [isPollingActive, setIsPollingActive] = useState(true);

  /**
   * Query for status polling
   */
  const { data, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['billing-status', billId],
    queryFn: () => fetchBillingStatus(billId as string),
    enabled: !policy && isPollingActive && Boolean(billId) && isProgressStatus && pollingCountRef.current < maxRequests,
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (!data) return;
    transactionStatusStore.setState({transactionStatus:data.transactionStatus})
    setResponse(data);
    pollingCountRef.current += 1;

    if (pollingCountRef.current >= maxRequests) {
      // Cancel polling
      queryClient.cancelQueries({ queryKey: ['billing-status', billId] });
      setIsPollingActive(false);
    }

    if (!data.transactionStatus) return;

    if (data.isExpiredBilling) {
        queryClient.invalidateQueries({
          queryKey: ["processInstance", processInstanceId],
        });
        return;
    }

    if (data.transactionStatus === TransactionStatusEnum.BILLING_FAILED) {
      queryClient.cancelQueries({ queryKey: ['billing-status', billId] });
      setIsPollingActive(false);
      closeLoadingModal();
      return;
    }

    if (data.transactionStatus === TransactionStatusEnum.FAIL_SINGING) {
      if (!isErrorModalClose.current) {
        openErrorModal({
          title: "Сталася помилка",
          description: "Шановний клієнте, під час обробки вашого замовлення сталась технічна помилка, тому договір не було надіслано на вашу електронну пошту.\n" +
              "Будь ласка, зателефонуйте за контактами інтернет-магазину або скористайтесь опцією «Написати нам».\n" +
              "Перепрошуємо за незручності та дякуємо за розуміння!",
          submitText: "Написати",
          onSubmit: () => {
            isErrorModalClose.current = true;
            closeErrorModal();
            openTextUsModal();
          }
        });
      }
      queryClient.cancelQueries({ queryKey: ['billing-status', billId] });
      setIsPollingActive(false);
      return;
    }

    if(data.transactionStatus === TransactionStatusEnum.SIGNING_IN_PROGRESS || data.transactionStatus === TransactionStatusEnum.BILLING_IN_PROGRESS) {
      openLoadingModal({
        title: loadingModalDefaultProps.title,
        description: "Опрацьовуємо ваш запит",
        disableClose: true,
      })
    }

    if (
      !hasNavigatedRef.current &&
      data?.transactionStatus === TransactionStatusEnum.CONTRACT_SIGNED
    ) {
      hasNavigatedRef.current = true;
      handleNavigateAfterBilling?.()
          .then(() => {
            closeLoadingModal();
            queryClient.cancelQueries({queryKey: ['billing-status', billId]});
          })
          .catch((e) => {
            logError('[useBillingPollingSSE] handleNavigateAfterBilling error (CONTRACT_SIGNED)', e);
            hasNavigatedRef.current = false;
          })
    }
  }, [data, dataUpdatedAt]);

  useEffect(() => {
    const isSuccessfulStatus = orderStatus === "COMPLETED_BY_CLIENT"

    // Force navigation if the status is successful
    if (isSuccessfulStatus && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      handleNavigateAfterBilling?.()
          .then(() => {
            closeLoadingModal();
            queryClient.cancelQueries({queryKey: ['billing-status', billId]});
          })
          .catch((e) => {
            logError('[useBillingPollingSSE] handleNavigateAfterBilling error (COMPLETED_BY_CLIENT)', e);
            hasNavigatedRef.current = false;
          })
    }
  }, [orderStatus, handleNavigateAfterBilling]);

  /**
   * SSE-consumer for billing status
   */
  useEffect(() => {
    if (!billId) return;

    const sseUrl = `${API_BASE_URL}${BASE_URLS.BILLING}/transaction-status/subscription/${billId}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      console.log("SSE [transaction] connection opened");
      // refetch needed to get the latest data from the server, before the SSE connection is opened. Sometimes connection is opened after the data is sent
      refetch();
      // pooling necessary as second option to get the latest data from the server if SSE event is not sent
      // setIsPollingActive(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const dataFromServer = JSON.parse(event.data);
        const newStatus = dataFromServer?.isSuccessful;
        console.log("SSE [transaction] data: ", dataFromServer);
        if (newStatus) {
          refetch();
        }
      } catch (err) {
        console.error("Помилка розбору SSE-повідомлення", err);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE [transaction] помилка: ", error);
      eventSource.close();
      // setIsPollingActive(true);
    };

    return () => {
      eventSource.close();
      // setIsPollingActive(false);
      console.log("SSE [transaction] connection closed");
    };
  }, [billId, refetch]);

  useEffect(() => {
    if (policy) {
      hasNavigatedRef.current = true;
    }
  }, [policy]);

  return {
    data,
    refetch,
  };
}
